import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  RefreshCw,
  Check,
  X,
  UserPlus,
  Users,
  Mic2,
  Calendar,
  Mail,
  Phone,
  Building2,
  Shield,
  KeyRound,
  UserX,
  UserCheck,
  Inbox,
  ImageIcon,
  Video,
  Music,
  FileText,
  Paperclip,
  Trash2,
  Send,
  AlertTriangle,
  Copy,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("sb_admin_token") || "";
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const KIND_ICON = {
  image: ImageIcon,
  video: Video,
  audio: Music,
  document: FileText,
  file: Paperclip,
};

const relTime = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const fmtBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

/**
 * AdminInbox — one page for every action an admin used to do across five
 * different tools. Three tabs:
 *   1. Approvals — pending clients, pending guests, pending bookings.
 *      Inline Approve / Decline on every row.
 *   2. Team     — admin credentials, invites, password resets, deactivate.
 *   3. Media    — cross-owner audit feed. Delete anything spammy from here.
 *
 * All existing dedicated pages (`/admin/bookings`, `/admin/guests`, etc.)
 * remain — this is a superset dashboard, not a replacement.
 */
const TABS = [
  { key: "approvals", label: "Approvals", icon: Check, color: "#22c55e" },
  { key: "team", label: "Team", icon: Shield, color: "#a78bfa" },
  { key: "media", label: "Media", icon: Paperclip, color: "#00f0ff" },
];

export default function AdminInbox() {
  const [tab, setTab] = useState("approvals");
  const [pendingClients, setPendingClients] = useState([]);
  const [pendingGuests, setPendingGuests] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [team, setTeam] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadApprovals = useCallback(async () => {
    try {
      const [c, g, b] = await Promise.all([
        axios.get(`${API}/admin/clients/pending`, { headers: authHeaders() }),
        axios.get(`${API}/guest-requests?status=pending`, { headers: authHeaders() }),
        axios.get(`${API}/bookings?status=pending_review`, { headers: authHeaders() }),
      ]);
      setPendingClients(c.data?.clients || []);
      setPendingGuests(Array.isArray(g.data) ? g.data : g.data?.items || []);
      setPendingBookings(Array.isArray(b.data) ? b.data : b.data?.items || []);
    } catch (e) {
      toast.error("Failed to load approvals queue");
    }
  }, []);

  const loadTeam = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/team`, { headers: authHeaders() });
      setTeam(data?.admins || []);
    } catch {
      toast.error("Failed to load team");
    }
  }, []);

  const loadMedia = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/media/recent?limit=100`, { headers: authHeaders() });
      setMedia(data?.files || []);
    } catch {
      toast.error("Failed to load media feed");
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadApprovals(), loadTeam(), loadMedia()]);
    setLoading(false);
  }, [loadApprovals, loadTeam, loadMedia]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Approve / decline actions ----
  const approveClient = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/clients/${id}/approve`, {}, { headers: authHeaders() });
      toast.success("Client approved — welcome invoice sent.");
      await loadApprovals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  };
  const declineClient = async (id) => {
    if (!window.confirm("Decline this client? They will be notified.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/clients/${id}/decline`, {}, { headers: authHeaders() });
      toast.success("Client declined.");
      await loadApprovals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decline failed");
    } finally { setBusy(false); }
  };
  const patchGuest = async (id, status) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/guest-requests/${id}`, { status }, { headers: authHeaders() });
      toast.success(`Guest ${status === "approved" ? "approved" : "declined"}.`);
      await loadApprovals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };
  const patchBooking = async (id, status) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/bookings/${id}`, { status }, { headers: authHeaders() });
      toast.success(`Booking ${status}.`);
      await loadApprovals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  // ---- Team actions ----
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const inviteAdmin = async () => {
    if (!inviteEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/team/invite`,
        { email: inviteEmail.trim(), name: inviteName.trim() },
        { headers: authHeaders() },
      );
      toast.success(`Invite sent to ${inviteEmail}.`);
      if (data?.accept_url) {
        try {
          await navigator.clipboard.writeText(data.accept_url);
          toast.info("Accept link copied.");
        } catch (err) {
          // Clipboard may be blocked (Safari without user gesture / no HTTPS).
          // Fall back to showing the link in the toast so the admin can copy manually.
          toast.info(`Accept link: ${data.accept_url}`);
          // eslint-disable-next-line no-console
          console.warn("clipboard write blocked", err);
        }
      }
      setInviteEmail(""); setInviteName("");
      await loadTeam();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };
  const sendReset = async (adminId) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/team/${adminId}/send-reset`, {}, { headers: authHeaders() });
      toast.success("Reset link sent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send-reset failed");
    } finally { setBusy(false); }
  };
  const toggleActive = async (a) => {
    setBusy(true);
    try {
      const path = a.is_active ? "deactivate" : "reactivate";
      await axios.post(`${API}/admin/team/${a.id}/${path}`, {}, { headers: authHeaders() });
      toast.success(`${a.email} ${a.is_active ? "deactivated" : "reactivated"}.`);
      await loadTeam();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  // ---- Media delete ----
  const deleteMedia = async (id) => {
    if (!window.confirm("Delete this file? Cannot be recovered.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/media/${id}`, { headers: authHeaders() });
      toast.success("File removed.");
      await loadMedia();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const approvalCount = pendingClients.length + pendingGuests.length + pendingBookings.length;

  const tabCounts = useMemo(() => ({
    approvals: approvalCount,
    team: team.length,
    media: media.length,
  }), [approvalCount, team.length, media.length]);

  return (
    <div className="min-h-screen bg-[#05050a] text-white relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/3 w-[520px] h-[520px] rounded-full bg-[#22c55e]/8 blur-[120px]" />
      </div>
      <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="min-w-0">
            <Link
              to="/admin"
              data-testid="inbox-back"
              className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 mb-3"
            >
              <ArrowLeft size={14} /> Admin Hub
            </Link>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter">
              Admin <span className="text-[#22c55e]">inbox.</span>
            </h1>
            <p className="text-[#a395c3] text-sm mt-2">
              One place for every approval, every login, every upload.
            </p>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="inbox-refresh"
            aria-label="Refresh"
            className="w-10 h-10 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center disabled:opacity-50 self-start"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="inbox-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            const n = tabCounts[t.key] || 0;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`inbox-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors ${
                  active
                    ? "bg-white text-[#05050a] border-white"
                    : "bg-white/[0.04] border-white/15 text-white/80 hover:bg-white/[0.08]"
                }`}
                style={active ? undefined : { color: undefined }}
              >
                <Icon size={13} style={{ color: active ? t.color : undefined }} />
                <span>{t.label}</span>
                {n > 0 && (
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-[#05050a] text-white" : ""}`}
                    style={active ? undefined : { background: `${t.color}22`, color: t.color }}
                  >
                    {n}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-white/60 py-10">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {tab === "approvals" && (
              <ApprovalsTab
                clients={pendingClients}
                guests={pendingGuests}
                bookings={pendingBookings}
                busy={busy}
                onApproveClient={approveClient}
                onDeclineClient={declineClient}
                onApproveGuest={(id) => patchGuest(id, "approved")}
                onDeclineGuest={(id) => patchGuest(id, "declined")}
                onApproveBooking={(id) => patchBooking(id, "approved")}
                onDeclineBooking={(id) => patchBooking(id, "declined")}
              />
            )}
            {tab === "team" && (
              <TeamTab
                team={team}
                inviteEmail={inviteEmail}
                inviteName={inviteName}
                setInviteEmail={setInviteEmail}
                setInviteName={setInviteName}
                busy={busy}
                onInvite={inviteAdmin}
                onSendReset={sendReset}
                onToggleActive={toggleActive}
              />
            )}
            {tab === "media" && (
              <MediaTab
                files={media}
                busy={busy}
                onDelete={deleteMedia}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------- Approvals tab ----------------
const ApprovalsTab = ({
  clients, guests, bookings, busy,
  onApproveClient, onDeclineClient,
  onApproveGuest, onDeclineGuest,
  onApproveBooking, onDeclineBooking,
}) => {
  const total = clients.length + guests.length + bookings.length;
  return (
    <div className="space-y-6" data-testid="inbox-approvals">
      {total === 0 && (
        <EmptyState
          icon={Inbox}
          title="Inbox zero."
          sub="Nothing waiting. Approvals will land here the moment they come in."
        />
      )}

      {clients.length > 0 && (
        <QueueBlock
          icon={UserPlus}
          color="#22c55e"
          label="Client member portal"
          sub="New signups asking for portal access. Approving auto-issues the welcome invoice."
          count={clients.length}
          testId="queue-clients"
        >
          {clients.map((c) => (
            <QueueRow
              key={c.id}
              testId={`inbox-client-${c.id}`}
              title={c.name || c.email}
              meta={[c.email, c.business_name, c.source].filter(Boolean).join(" · ")}
              hint={c.message ? `“${c.message.slice(0, 140)}${c.message.length > 140 ? "…" : ""}”` : ""}
              createdAt={c.created_at}
              onApprove={() => onApproveClient(c.id)}
              onDecline={() => onDeclineClient(c.id)}
              busy={busy}
            />
          ))}
        </QueueBlock>
      )}

      {guests.length > 0 && (
        <QueueBlock
          icon={Mic2}
          color="#ff0055"
          label="Podcast guests"
          sub="Spotlight applicants — Saturday slots hold in their name until you decide."
          count={guests.length}
          testId="queue-guests"
        >
          {guests.map((g) => (
            <QueueRow
              key={g.id}
              testId={`inbox-guest-${g.id}`}
              title={g.name || g.email}
              meta={[g.email, g.preferred_date, g.time_slot].filter(Boolean).join(" · ")}
              hint={g.project_brief ? `“${g.project_brief.slice(0, 140)}${g.project_brief.length > 140 ? "…" : ""}”` : ""}
              createdAt={g.created_at}
              onApprove={() => onApproveGuest(g.id)}
              onDecline={() => onDeclineGuest(g.id)}
              busy={busy}
            />
          ))}
        </QueueBlock>
      )}

      {bookings.length > 0 && (
        <QueueBlock
          icon={Calendar}
          color="#00f0ff"
          label="Studio bookings"
          sub="Paid session requests awaiting confirmation."
          count={bookings.length}
          testId="queue-bookings"
        >
          {bookings.map((b) => (
            <QueueRow
              key={b.id}
              testId={`inbox-booking-${b.id}`}
              title={b.name || b.email}
              meta={[b.email, b.preferred_date, b.package_label, b.budget && `$${b.budget}`].filter(Boolean).join(" · ")}
              hint={b.project_brief ? `“${b.project_brief.slice(0, 140)}${b.project_brief.length > 140 ? "…" : ""}”` : ""}
              createdAt={b.created_at}
              onApprove={() => onApproveBooking(b.id)}
              onDecline={() => onDeclineBooking(b.id)}
              busy={busy}
            />
          ))}
        </QueueBlock>
      )}
    </div>
  );
};

const QueueBlock = ({ icon: Icon, color, label, sub, count, testId, children }) => (
  <div
    className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-5 md:p-7"
    data-testid={testId}
  >
    <div className="flex items-start gap-3 mb-5">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center border flex-shrink-0"
        style={{ background: `${color}18`, borderColor: `${color}45`, color }}
      >
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color }}>
          {label} · {count}
        </div>
        <div className="text-[#a395c3] text-xs mt-1 leading-relaxed">{sub}</div>
      </div>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const QueueRow = ({ testId, title, meta, hint, createdAt, onApprove, onDecline, busy }) => (
  <div
    data-testid={testId}
    className="flex flex-col md:flex-row md:items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/10"
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-sm truncate">{title}</span>
        {createdAt && (
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
            {relTime(createdAt)} ago
          </span>
        )}
      </div>
      {meta && (
        <div className="text-[11px] text-white/50 mt-0.5 truncate">{meta}</div>
      )}
      {hint && (
        <div className="text-[11px] text-white/70 mt-1.5 italic line-clamp-2">{hint}</div>
      )}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <button
        type="button"
        disabled={busy}
        onClick={onApprove}
        data-testid={`${testId}-approve`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/40 text-[#22c55e] text-xs font-semibold hover:bg-[#22c55e]/25 disabled:opacity-50"
      >
        <Check size={12} /> Approve
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={onDecline}
        data-testid={`${testId}-decline`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ef4444]/10 border border-[#ef4444]/40 text-[#ef4444] text-xs font-semibold hover:bg-[#ef4444]/20 disabled:opacity-50"
      >
        <X size={12} /> Decline
      </button>
    </div>
  </div>
);

// ---------------- Team tab ----------------
const TeamTab = ({
  team, inviteEmail, inviteName, setInviteEmail, setInviteName,
  busy, onInvite, onSendReset, onToggleActive,
}) => (
  <div className="space-y-6" data-testid="inbox-team">
    <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-5 md:p-7">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#a78bfa]/15 border border-[#a78bfa]/40 text-[#a78bfa] flex items-center justify-center">
          <UserPlus size={16} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#a78bfa]">
            Invite an admin
          </div>
          <div className="text-[#a395c3] text-xs mt-1">
            Sends a magic link they use to set their own password. No shared secrets.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
        <input
          type="email"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          placeholder="teammate@sbmediastudio.com"
          data-testid="inbox-invite-email"
          className="px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/15 text-sm text-white focus:outline-none focus:border-[#a78bfa]/50"
        />
        <input
          type="text"
          value={inviteName}
          onChange={(e) => setInviteName(e.target.value)}
          placeholder="Name (optional)"
          data-testid="inbox-invite-name"
          className="px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/15 text-sm text-white focus:outline-none focus:border-[#a78bfa]/50"
        />
        <button
          type="button"
          disabled={busy || !inviteEmail.trim()}
          onClick={onInvite}
          data-testid="inbox-invite-submit"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#a78bfa] text-[#05050a] font-semibold text-sm hover:bg-white transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Invite
        </button>
      </div>
    </div>

    <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-5 md:p-7">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#00f0ff]/15 border border-[#00f0ff]/40 text-[#00f0ff] flex items-center justify-center">
          <Users size={16} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#00f0ff]">
            Admin team · {team.length}
          </div>
          <div className="text-[#a395c3] text-xs mt-1">
            Password resets, deactivations — every admin credential lives here.
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {team.length === 0 && (
          <div className="text-white/40 italic text-sm py-2">No admins yet.</div>
        )}
        {team.map((a) => (
          <div
            key={a.id}
            data-testid={`inbox-team-row-${a.id}`}
            className="flex flex-col md:flex-row md:items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/10"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm truncate">{a.name || a.email}</span>
                {!a.is_active && (
                  <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border bg-[#ef4444]/10 text-[#ef4444] border-[#ef4444]/40">
                    Deactivated
                  </span>
                )}
                {a.is_active && (
                  <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/40">
                    Active
                  </span>
                )}
              </div>
              <div className="text-[11px] text-white/50 mt-0.5 truncate">
                {a.email}
                {a.last_login_at && <> · Last login {new Date(a.last_login_at).toLocaleDateString()}</>}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                disabled={busy}
                onClick={() => onSendReset(a.id)}
                data-testid={`inbox-team-reset-${a.id}`}
                title="Send password reset"
                aria-label="Send password reset"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 text-xs hover:bg-white/[0.1] disabled:opacity-50"
              >
                <KeyRound size={12} /> Reset
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => onToggleActive(a)}
                data-testid={`inbox-team-toggle-${a.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs disabled:opacity-50 border ${
                  a.is_active
                    ? "bg-[#ef4444]/10 border-[#ef4444]/40 text-[#ef4444] hover:bg-[#ef4444]/20"
                    : "bg-[#22c55e]/10 border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/20"
                }`}
              >
                {a.is_active ? <UserX size={12} /> : <UserCheck size={12} />}
                {a.is_active ? "Deactivate" : "Reactivate"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ---------------- Media tab ----------------
const MediaTab = ({ files, busy, onDelete }) => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-5 md:p-7" data-testid="inbox-media">
    <div className="flex items-start gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl bg-[#00f0ff]/15 border border-[#00f0ff]/40 text-[#00f0ff] flex items-center justify-center">
        <Paperclip size={16} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#00f0ff]">
          Recent uploads · {files.length}
        </div>
        <div className="text-[#a395c3] text-xs mt-1">
          Every file uploaded by admins, clients, and public guest applicants.
          Delete anything that looks like spam.
        </div>
      </div>
    </div>
    <div className="space-y-2">
      {files.length === 0 && (
        <div className="text-white/40 italic text-sm py-2">Nothing uploaded yet.</div>
      )}
      {files.map((f) => {
        const Icon = KIND_ICON[f.kind] || Paperclip;
        const token = getToken();
        const src = `${API}${f.url}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
        return (
          <div
            key={f.id}
            data-testid={`inbox-media-row-${f.id}`}
            className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.02] border border-white/10"
          >
            <div className="w-11 h-11 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0 bg-white/[0.05] border border-white/10">
              {f.kind === "image" ? (
                <img src={src} alt={f.filename} loading="lazy" className="w-full h-full object-cover" />
              ) : (
                <Icon size={16} className="text-white/60" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium truncate">{f.filename}</div>
              <div className="text-[10px] text-white/40 mt-0.5 truncate">
                {f.owner_type}/{f.owner_id} · {fmtBytes(f.size)} · {f.uploaded_by_role} · {relTime(f.created_at)} ago
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`inbox-media-open-${f.id}`}
                aria-label="Open file"
                className="w-8 h-8 rounded-lg bg-white/[0.05] border border-white/10 hover:bg-[#00f0ff]/15 hover:text-[#00f0ff] hover:border-[#00f0ff]/40 flex items-center justify-center transition-colors"
              >
                <ArrowRight size={13} />
              </a>
              <button
                type="button"
                disabled={busy}
                onClick={() => onDelete(f.id)}
                data-testid={`inbox-media-delete-${f.id}`}
                aria-label="Delete file"
                className="w-8 h-8 rounded-lg bg-white/[0.05] border border-white/10 hover:bg-[#ff0055]/15 hover:text-[#ff0055] hover:border-[#ff0055]/40 flex items-center justify-center transition-colors disabled:opacity-50"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ---------------- Empty state ----------------
const EmptyState = ({ icon: Icon, title, sub }) => (
  <div className="rounded-3xl border border-dashed border-white/15 bg-white/[0.015] p-10 text-center" data-testid="inbox-empty">
    <div className="mx-auto w-14 h-14 rounded-2xl bg-[#22c55e]/12 border border-[#22c55e]/30 flex items-center justify-center mb-4">
      <Icon size={22} className="text-[#22c55e]" />
    </div>
    <div className="font-display font-bold text-xl tracking-tight">{title}</div>
    <div className="text-[#a395c3] text-sm mt-2 max-w-md mx-auto">{sub}</div>
  </div>
);
